// Sanitet & personell (PERSTAT): levende tap-liste + stridsevne-sammendrag.
// Opprett tap (status/triage/MEDEVAC), se sortert etter alvorlighet, deles i
// sanntid over hubben. Klikk-til-kart. Docked GNÅ-panel, ren DOM.
//
// FORHÅNDSUTFYLLING (se `kontekst.ts`): avsender, enhet, DTG og posisjon står
// ferdig utfylt fra innlogget bruker + egen posisjon + tid. Tidligere ba dette
// skjemaet om ENHET som fritekst — i et system som kjenner operatørens
// ORBAT-plassering og viser den øverst til høyre. Det ENESTE feltet som fortsatt
// er tomt, er kallesignalet til den skadde: hvem som er truffet kan systemet
// ikke vite, og et gjettet navn på en KIA-melding er utilgivelig.
import {
  CasualtySchema, sortCasualties, perstat,
  PERSONNEL_STATUS_LABEL, PERSONNEL_STATUS_COLOR, TRIAGE_LABEL, TRIAGE_COLOR,
  type Casualty, type PersonnelStatus, type TriageCategory,
} from '@skade/contracts';
import { KONTEKST_FELT, type Kontekst } from '../kontekst.js';
import { KontekstBlokk } from './kontekstblokk.js';

const STATUSES: PersonnelStatus[] = ['ok', 'wia', 'kia', 'mia', 'captured'];
const TRIAGES: TriageCategory[] = ['t1', 't2', 't3', 't4', 'dead'];

export interface CasualtyHooks {
  self: string;
  getCasualties: () => Casualty[];
  publish: (c: Casualty) => void;
  focus: (c: Casualty) => void;
  /** Ferdig kontekst (hvem/hvor/når/enhet) — ÉN kilde, ikke ad hoc-oppslag. */
  kontekst: () => Kontekst;
  /** Tida sendes inn; panelet leser ikke klokka selv. */
  naa: () => number;
}

export class CasualtyPanel {
  readonly el: HTMLElement;
  private open = false;
  private sumEl!: HTMLElement;
  private listEl!: HTMLElement;
  private blokk!: KontekstBlokk;
  private submitBtn!: HTMLButtonElement;

  constructor(private hooks: CasualtyHooks) {
    this.el = this.build();
    this.render();
  }

  toggle() {
    this.open = !this.open;
    this.el.classList.toggle('open', this.open);
    // Frisk opp DTG/posisjon ved hver åpning — ellers står klokka og koordinatet
    // stille fra første gang panelet ble bygget.
    if (this.open) { this.blokk.oppfrisk(); this.merkSubmit(); this.render(); }
  }

  private build(): HTMLElement {
    const p = div('report-composer casualty-panel');
    const head = div('report-head');
    head.append(span('report-title', 'PERSTAT / sanitet'));
    const x = document.createElement('button');
    x.className = 'report-x'; x.textContent = '×'; x.onclick = () => this.toggle();
    head.append(x);
    p.append(head);
    this.sumEl = div('casualty-sum');
    p.append(this.sumEl);

    const form = div('report-form');
    // Kontekst-blokka FØRST: avsender/enhet/DTG/posisjon ferdig utfylt og merket
    // med posisjonens kilde. Erstatter det gamle frie «Enhet / lag»-feltet.
    this.blokk = new KontekstBlokk({ hent: () => this.hooks.kontekst(), posisjonLabel: 'Posisjon (tapets sted)' });
    form.append(this.blokk.el);
    const cs = document.createElement('input');
    cs.className = 'report-input'; cs.placeholder = 'Kallesignal / navn';
    const statusSel = sel(STATUSES, (s) => PERSONNEL_STATUS_LABEL[s as PersonnelStatus]);
    const triageSel = sel(['', ...TRIAGES], (t) => t ? TRIAGE_LABEL[t as TriageCategory] : 'Triage (valgfri)');
    const med = document.createElement('label'); med.className = 'casualty-check';
    const medCb = document.createElement('input'); medCb.type = 'checkbox';
    med.append(medCb, document.createTextNode(' Krever MEDEVAC'));
    const submit = document.createElement('button');
    this.submitBtn = submit;
    submit.className = 'report-submit';
    submit.onclick = () => {
      if (!cs.value.trim()) return;
      const now = this.hooks.naa();
      const parsed = CasualtySchema.safeParse({
        id: `cas-${now.toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`,
        callsign: cs.value.trim(), status: statusSel.value as PersonnelStatus,
        triage: triageSel.value || undefined,
        // Enheten kommer fra ORBAT-plasseringen via kontekst-blokka (redigerbar),
        // ikke fra et blankt fritekstfelt operatøren må fylle på nytt hver gang.
        unit: this.blokk.verdi(KONTEKST_FELT.enhet),
        medevac: medCb.checked, reportedBy: this.hooks.self, tsMs: now, updatedMs: now,
      });
      if (parsed.success) {
        this.hooks.publish(parsed.data);
        // Bare kallesignalet nullstilles: enhet/avsender gjelder fortsatt for
        // neste tap, og DTG friskes opp. Å tømme alt ville tvunget operatøren til
        // å skrive samme enhet om igjen for hvert tap i samme trefning.
        cs.value = ''; medCb.checked = false;
        this.blokk.oppfrisk();
        this.render();
      }
    };
    form.append(
      labeled('Kallesignal (den skadde)', cs),
      span('checklist-note', 'Kallesignal er det eneste systemet ikke kan vite — skriv det inn.'),
      labeled('Status', statusSel), labeled('Triage', triageSel), med, submit,
    );
    p.append(form);
    this.merkSubmit();

    this.listEl = div('targetlist-list');
    p.append(this.listEl);
    return p;
  }

  /**
   * Knappeteksten SIER hvilken posisjon tapet festes til. «Registrer (på
   * kartsenter)» sto der før uansett hva som var sant — også når GPS-en var på.
   * Nå følger teksten kilden, og er posisjonen ukjent, står det at tapet
   * registreres UTEN posisjon i stedet for å antyde et sted.
   */
  private merkSubmit() {
    const k = this.hooks.kontekst();
    this.submitBtn.textContent = k.posKilde === 'gps'
      ? 'Registrer (på egen GPS-posisjon)'
      : k.posKilde === 'kartsenter'
        ? 'Registrer (på KARTSENTER)'
        : 'Registrer (uten posisjon)';
  }

  render() {
    const all = this.hooks.getCasualties();
    const p = perstat(all);
    this.sumEl.innerHTML = '';
    const chip = (label: string, n: number, color: string) => {
      const c = document.createElement('span');
      c.className = 'casualty-chip'; c.style.borderColor = color; c.style.color = color;
      c.textContent = `${label} ${n}`;
      return c;
    };
    this.sumEl.append(
      chip('OK', p.ok, PERSONNEL_STATUS_COLOR.ok),
      chip('WIA', p.wia, PERSONNEL_STATUS_COLOR.wia),
      chip('KIA', p.kia, PERSONNEL_STATUS_COLOR.kia),
      chip('MIA', p.mia, PERSONNEL_STATUS_COLOR.mia),
      chip('MEDEVAC', p.medevac, '#80e0ff'),
    );

    this.listEl.innerHTML = '';
    const items = sortCasualties(all);
    if (!items.length) { this.listEl.append(span('checklist-note', 'Ingen tap registrert.')); return; }
    for (const c of items) {
      const row = div('targetlist-row');
      const dot = document.createElement('span');
      dot.className = 'checklist-dot';
      dot.style.background = c.triage ? TRIAGE_COLOR[c.triage] : PERSONNEL_STATUS_COLOR[c.status];
      const main = div('checklist-main');
      const title = span('checklist-item-title', c.callsign + (c.unit ? ` · ${c.unit}` : ''));
      if (c.lat !== undefined && c.lon !== undefined) { title.style.cursor = 'pointer'; title.onclick = () => this.hooks.focus(c); }
      main.append(title);
      const bits = [PERSONNEL_STATUS_LABEL[c.status]];
      if (c.triage) bits.push(TRIAGE_LABEL[c.triage]);
      if (c.medevac) bits.push('MEDEVAC');
      main.append(span('checklist-note', bits.join(' · ')));
      row.append(dot, main);
      this.listEl.append(row);
    }
  }
}

function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
function labeled(label: string, control: HTMLElement): HTMLElement {
  const wrap = div('report-field'); wrap.append(span('report-field-label', label), control); return wrap;
}
function sel(options: string[], label: (v: string) => string): HTMLSelectElement {
  const s = document.createElement('select'); s.className = 'checklist-sel';
  for (const o of options) { const opt = document.createElement('option'); opt.value = o; opt.textContent = label(o); s.append(opt); }
  return s;
}
